import React, { useEffect, useState } from 'react'
import { Card, Flex, Typography } from 'antd'
import { CFormSelect, CSmartTable } from '@coreui/react-pro'
import { GatewaysDetailsItemTable, HistoryItem } from './types'
import { GatewaysDetailsHistoryModal } from '../GatewaysDetailsHistoryModal/component'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'
import {
  $gatewaysDetails,
  gatewaysDetailsMounted,
  $totalCountOfGatewaysDetail,
} from './model'
import {
  changedDateFormat,
  getFinsystemNumberFormat,
} from '../../../../../libs/utils'

const columns = [
  {
    key: 'gatewayId',
    label: 'ID',
  },
  {
    key: 'name',
    label: 'Internal name',
  },
  {
    key: 'currencyCode',
    label: 'Currency',
  },
  {
    key: 'deposit',
    label: 'Deposit',
  },
  {
    key: 'withdrawal',
    label: 'Withdrawal',
  },
  {
    key: 'lossPercentage',
    label: 'Loss',
  },
  {
    key: 'connectedAt',
    label: 'First connection',
  },
  {
    key: 'conditions',
    label: 'Conditions',
  },
  {
    key: 'depositAmountUsd',
    label: 'Deposit USD',
  },
  {
    key: 'withdrawalAmountUsd',
    label: 'Withdrawal USD',
  },
]

type Props = {
  paymentSystemId: string
}

export const GatewaysDetails: React.FC<Props> = ({ paymentSystemId }) => {
  const gatewaysDetail = useStore($gatewaysDetails)
  const totalCountOfGatewaysDetail = useStore($totalCountOfGatewaysDetail)

  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(3)

  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedHistoryData, setSelectedHistoryData] = useState<HistoryItem[]>(
    [],
  )

  const handleOpenModal = (item: GatewaysDetailsItemTable) => {
    const historyMapperData = item.gatewayHistories.map((item) => ({
      status: item.event,
      date: dayjs(item.createdAt).format('DD.MM.YYYY'),
      time: dayjs(item.createdAt).format('HH:mm:ss'),
    }))
    setIsOpenModal(true)
    setSelectedHistoryData(historyMapperData)
  }

  useEffect(() => {
    if (paymentSystemId) {
      gatewaysDetailsMounted({
        paymentSystemId: paymentSystemId,
        limit: itemsPerPage,
        page: activePage,
      })
    }
  }, [activePage, itemsPerPage, paymentSystemId])

  return (
    <Card>
      <Flex vertical>
        <Flex justify="space-between">
          <div>
            <Typography.Title level={4}>Gateways</Typography.Title>
          </div>
          <Flex gap={5} align="center">
            <CFormSelect label="View">
              <option>Last 30 days</option>
            </CFormSelect>
          </Flex>
        </Flex>
        <CSmartTable
          columns={columns}
          items={gatewaysDetail.map((item) => ({
            gatewayId: item.gatewayId,
            name: item.name,
            currencyCode: item.currencyCode,
            lossPercentage: `-${getFinsystemNumberFormat(
              item.lossPercentage,
              2,
            )}%`,
            withdrawal: item.withdrawal,
            deposit: item.deposit,
            withdrawalAmountUsd: parseFloat(item.withdrawalAmountUsd).toFixed(
              2,
            ),
            depositAmountUsd: parseFloat(item.depositAmountUsd).toFixed(2),
            gatewayHistories: item.gatewayHistories,
            conditions: item.conditions,
            connectedAt: changedDateFormat(new Date(item.connectedAt)),
          }))}
          tableProps={{
            striped: true,
            responsive: true,
          }}
          itemsPerPage={itemsPerPage}
          itemsPerPageOptions={[3, 5, 10, 20, 50]}
          itemsPerPageSelect
          onItemsPerPageChange={(itemsPerPage) => {
            setActivePage(1)
            setItemsPerPage(itemsPerPage)
          }}
          pagination={{
            external: true,
          }}
          paginationProps={{
            activePage: activePage,
            pages: Math.ceil(totalCountOfGatewaysDetail / itemsPerPage) || 1,
          }}
          onActivePageChange={(activePage) => setActivePage(activePage)}
          scopedColumns={{
            connectedAt: (item: GatewaysDetailsItemTable) => (
              <td>
                <Flex vertical gap={16}>
                  <div>{item.connectedAt}</div>
                  <a
                    style={{ color: '#321FBD' }}
                    onClick={() => handleOpenModal(item)}
                  >
                    Open history
                  </a>
                </Flex>
              </td>
            ),
          }}
        />
      </Flex>
      <GatewaysDetailsHistoryModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        data={selectedHistoryData}
      />
    </Card>
  )
}
